import React from "react";

function CodeBlock({ text, label = "Copy" }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };
  return (
    <div className="stt-code">
      <pre className="stt-pre" aria-label="code snippet">
        <code>{text}</code>
      </pre>
      <button className="stt-btn stt-btn-outline" onClick={copy}>
        {copied ? "Copied!" : label}
      </button>
    </div>
  );
}

export default function SofttenantDocs() {
  const installText = `pip install softtenant`;

  const setupText = `from fastapi import FastAPI
from sqlalchemy import create_engine
from softtenant import TenantMiddleware, TenantSession

engine = create_engine("postgresql://user:pass@localhost/mydb")

app = FastAPI()

# Resolves tenant from X-Tenant-ID header (or JWT claim)
app.add_middleware(TenantMiddleware, engine=engine, resolver="header")`;

  const queryText = `from fastapi import Depends
from softtenant import get_tenant_session
from sqlalchemy.orm import Session
from .models import Order

# All queries are automatically scoped to the current tenant
@app.get("/orders")
def list_orders(db: Session = Depends(get_tenant_session)):
    return db.query(Order).all()   # only this tenant's rows returned`;

  const jwtText = `# Resolve tenant from a JWT claim instead of a header
app.add_middleware(
    TenantMiddleware,
    engine=engine,
    resolver="jwt",
    jwt_claim="tenant_id",   # key inside the token payload
    jwt_secret="your-secret",
)`;

  const migrationText = `# Your existing models need NO changes.
# softtenant adds a tenant_id column via a SQLAlchemy mixin:

from softtenant import TenantMixin
from sqlalchemy.orm import DeclarativeBase

class Base(DeclarativeBase):
    pass

class Order(TenantMixin, Base):
    __tablename__ = "orders"
    # all your existing columns stay the same`;

  return (
    <main className="docs-wrap">
      <div className="docs-layout">
        <aside className="docs-toc">
          <strong>On this page</strong>
          <a href="#intro">Introduction</a>
          <a href="#install">Install</a>
          <a href="#setup">Middleware Setup</a>
          <a href="#queries">Scoped Queries</a>
          <a href="#jwt">JWT Resolver</a>
          <a href="#models">Models & Migration</a>
        </aside>

        <article>
          <header className="stt-hero">
            <div className="stt-pill">softtenant • Docs</div>
            <h1 className="stt-h1">Multi-tenancy with zero schema rewrites</h1>
            <p className="stt-lead">
              Drop multi-tenancy into existing FastAPI + SQLAlchemy apps via middleware —
              automatic tenant resolution, scoped queries, no model changes required.
            </p>
          </header>

          <section id="intro" className="stt-section">
            <h2 className="stt-h2">What is softtenant?</h2>
            <p className="stt-p">
              softtenant adds <strong>row-level multi-tenancy</strong> to existing apps without rewriting
              your schema. A single middleware intercepts each request, resolves the tenant from a header
              or JWT claim, and scopes every SQLAlchemy query automatically — no <code>WHERE tenant_id = ?</code>
              scattered through your code.
            </p>
          </section>

          <section id="install" className="stt-section stt-card">
            <h2 className="stt-card-title">Install</h2>
            <div className="stt-card-body">
              <CodeBlock text={installText} label="Copy" />
            </div>
          </section>

          <section id="setup" className="stt-section stt-card">
            <h2 className="stt-card-title">Middleware Setup</h2>
            <div className="stt-card-body">
              <p className="stt-p">
                Add <code>TenantMiddleware</code> once. It reads the tenant identifier from an
                <code> X-Tenant-ID</code> header by default.
              </p>
              <CodeBlock text={setupText} label="Copy snippet" />
            </div>
          </section>

          <section id="queries" className="stt-section stt-card">
            <h2 className="stt-card-title">Scoped Queries</h2>
            <div className="stt-card-body">
              <p className="stt-p">
                Swap <code>get_db</code> for <code>get_tenant_session</code> — every query is
                automatically filtered to the current tenant's rows.
              </p>
              <CodeBlock text={queryText} label="Copy snippet" />
            </div>
          </section>

          <section id="jwt" className="stt-section stt-card">
            <h2 className="stt-card-title">JWT Resolver</h2>
            <div className="stt-card-body">
              <p className="stt-p">
                Resolve the tenant from a claim inside a JWT bearer token instead of a plain header.
              </p>
              <CodeBlock text={jwtText} label="Copy snippet" />
            </div>
          </section>

          <section id="models" className="stt-section stt-grid-2">
            <article className="stt-card">
              <h2 className="stt-card-title">Models & Migration</h2>
              <div className="stt-card-body">
                <CodeBlock text={migrationText} label="Copy snippet" />
              </div>
            </article>
            <article className="stt-card">
              <h2 className="stt-card-title">How it works</h2>
              <div className="stt-card-body">
                <ul className="stt-list">
                  <li><code>TenantMixin</code> adds a <code>tenant_id</code> column to your model.</li>
                  <li>The middleware stores the resolved tenant in a context var per request.</li>
                  <li>A SQLAlchemy query event injects <code>WHERE tenant_id = current_tenant</code> automatically.</li>
                  <li>Zero changes to existing business logic or route handlers.</li>
                </ul>
              </div>
            </article>
          </section>
        </article>
      </div>
    </main>
  );
}
